export const PLAN_CHOICES = [
  { key: 'wholeBible', title: 'Whole Bible' },
  { key: 'pentateuch', title: 'Pentateuch' },
  { key: 'oldTestament', title: 'Old Testament' },
  { key: 'newTestament', title: 'New Testament' },
  { key: 'gospels', title: 'Gospels' },
  { key: 'wisdom', title: 'Wisdom Literature' },
  { key: 'custom', title: 'Custom' }
];

export const CUSTOM_KINDS = [
  { key: 'chapters', title: 'Chapters' },
  { key: 'bibles', title: 'Bibles' },
  { key: 'books', title: 'Books' }
];

export const modeLabel = (mode) => (mode === 'pooled' ? 'Pooling' : 'Same plan');

export const daysLeftLabel = (daysLeft) => {
  if (daysLeft == null) return null;
  if (daysLeft <= 0) return 'Last day';
  if (daysLeft === 1) return '1 day left';
  return `${daysLeft} days left`;
};

export const paceChip = (delta) => {
  if (delta == null || Number.isNaN(Number(delta))) return null;
  const value = Number(delta);
  if (value > 0) return { value: `+${value.toLocaleString()}`, label: 'ahead' };
  if (value < 0) return { value: value.toLocaleString(), label: 'behind' };
  return { value: '0', label: 'on pace' };
};

export const percentOf = (read, total) => (
  total > 0 ? Math.round((Math.min(read, total) / total) * 100) : 0
);

export const planDraft = ({
  goal,
  mode,
  customKind,
  chapterTarget,
  bibleTarget,
  bookNames
}) => ({
  goal,
  mode: mode === 'pooled' ? 'pooled' : 'same',
  customKind: CUSTOM_KINDS.some((kind) => kind.key === customKind) ? customKind : 'chapters',
  chapterTarget: Math.min(1189, Math.max(1, Number(chapterTarget) || 1)),
  bibleTarget: Math.min(10, Math.max(1, Number(bibleTarget) || 1)),
  bookNames: bookNames || 'Genesis'
});

const focusRow = (status, myReaderIds) => {
  if (status.plan.mode === 'pooled') return status.pooled;
  const mine = new Set(myReaderIds);
  const rows = status.members || [];
  return rows.find((row) => mine.has(row.readerId)) || rows[0] || null;
};

/**
 * Numbers for the family plan hero. The API already counted the reading.
 * Pooled mode uses the combined family total. Same plan uses this account's
 * reader when one is in the family.
 */
export const planHero = (status, myReaderIds = []) => {
  const plan = status?.plan;
  if (!plan) return { empty: true };

  const focus = focusRow(status, myReaderIds);
  const read = focus?.read || 0;
  const total = focus?.total || plan.total || 0;
  const daysLabel = daysLeftLabel(status.window?.daysLeft);

  return {
    empty: false,
    title: plan.title,
    mode: plan.mode,
    modeLabel: modeLabel(plan.mode),
    percent: percentOf(read, total),
    read,
    total,
    delta: focus?.delta ?? null,
    pace: paceChip(focus?.delta),
    missedDays: Boolean(focus?.missedDays),
    daysLeft: status.window?.daysLeft ?? null,
    daysLabel,
    caption: [plan.title, modeLabel(plan.mode), daysLabel].filter(Boolean).join(' · ')
  };
};

/**
 * One row per person in the family. Readers carry the numbers, joined to stats
 * and plan rows by reader id. An account with no primary reader here still gets
 * a name-only row. Rows live in a Map so nobody can appear twice.
 */
export const memberRows = ({ family, stats, plan, userId, myReaderIds = [] }) => {
  if (!family) return [];
  const isOwner = Boolean(userId && family.createdBy === userId);
  const mine = new Set(myReaderIds);
  const statsById = new Map((stats?.members || []).map((member) => [member.readerId, member]));
  const planById = new Map((plan?.plan ? (plan.members || []) : []).map((row) => [row.readerId, row]));
  const order = new Map((stats?.members || []).map((member, index) => [member.readerId, index]));
  const rows = new Map();

  for (const reader of family.readers || []) {
    const counted = statsById.get(reader.id);
    const planned = planById.get(reader.id);
    const other = Boolean(reader.userId) && reader.userId !== userId;
    rows.set(reader.id, {
      key: reader.id,
      name: reader.name,
      readerId: reader.id,
      userId: reader.userId || null,
      isOwner: Boolean(reader.isPrimary && reader.userId === family.createdBy),
      isMine: mine.has(reader.id),
      hasStats: Boolean(counted),
      chapters: counted?.chapters || 0,
      seasonChapters: counted?.seasonChapters || 0,
      streak: counted?.streak || 0,
      percent: planned ? percentOf(planned.read || 0, planned.total || 0) : null,
      pace: planned ? paceChip(planned.delta) : null,
      canManage: Boolean(isOwner && other && reader.isPrimary),
      canRemoveReader: Boolean(mine.has(reader.id) && !reader.isPrimary)
    });
  }

  const withPrimary = new Set(
    (family.readers || []).filter((reader) => reader.isPrimary).map((reader) => reader.userId)
  );
  for (const member of family.members || []) {
    if (withPrimary.has(member.id)) continue;
    const key = `user:${member.id}`;
    if (rows.has(key)) continue;
    rows.set(key, {
      key,
      name: member.name,
      readerId: null,
      userId: member.id,
      isOwner: member.id === family.createdBy,
      isMine: member.id === userId,
      hasStats: false,
      chapters: 0,
      seasonChapters: 0,
      streak: 0,
      percent: null,
      pace: null,
      canManage: Boolean(isOwner && member.id !== userId),
      canRemoveReader: false
    });
  }

  const place = (row) => (order.has(row.readerId) ? order.get(row.readerId) : Number.MAX_SAFE_INTEGER);
  return [...rows.values()].sort((a, b) => place(a) - place(b));
};
